// Secciones de configuración de Admin · Integración de planta: mapeos,
// parámetros, fuente de captura por línea y peso por SKU.
//
// Fuente de captura y peso: el valor vigente viene de GET
// /api/admin/ingesta/resumen y se muestra como insignia o como texto de ayuda
// del campo. Los formularios ESCRIBEN: un campo vacío o «Sin cambios» no se
// envía, y tras guardar se muestra el valor que devolvió el servidor.

import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type {
  AdminCausalesList,
  AdminIngestaResumen,
  AdminEstructura,
  FuenteCapturaLinea,
  IngestaConfig,
  MapeoIngesta,
  SkuPesoUpdate,
  TipoMapeoIngesta,
} from 'schemas';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { SegmentedControl } from '../../../components/ui/segmented-control';
import { Select } from '../../../components/ui/select';
import { TBody, TD, TH, THead, TR, Table } from '../../../components/ui/table';
import {
  useActualizarFuenteCapturaLinea,
  useActualizarPesoSku,
  useAdminIngestaResumen,
  useCrearMapeoIngesta,
  useEliminarMapeoIngesta,
  useGuardarIngestaConfig,
  useMapeosIngesta,
  type LineaFuenteCapturaResultado,
  type SkuPesoResultado,
} from '../../../lib/api/ingesta';
import { useSkusByLinea } from '../../../lib/api/queries';
import { ETIQUETA_FUENTE } from '../../coordinador/integracion/etiquetas';
import { Seccion, mensajeError } from './comun';

export interface OpcionLinea {
  id: string;
  nombre: string;
  codigo: string;
  planta: string;
}

/** Líneas activas del tenant, aplanadas desde la estructura. */
export function lineasDeEstructura(e: AdminEstructura | undefined): OpcionLinea[] {
  if (!e) return [];
  const out: OpcionLinea[] = [];
  for (const p of e.plantas)
    for (const a of p.areas)
      for (const l of a.lineas)
        if (l.activa) out.push({ id: l.id, nombre: l.nombre, codigo: l.codigo, planta: p.nombre });
  return out;
}

export interface OpcionCausal {
  id: string;
  etiqueta: string;
}

/** Causales específicas activas como «Madre → Específica». */
export function causalesEspecificas(c: AdminCausalesList | undefined): OpcionCausal[] {
  if (!c) return [];
  const out: OpcionCausal[] = [];
  for (const m of c.causales) {
    if (!m.activa) continue;
    for (const h of m.hijas)
      if (h.activa) out.push({ id: h.id, etiqueta: `${m.nombre} → ${h.nombre}` });
  }
  return out;
}

// ─── Mapeos ─────────────────────────────────────────────────────────────────

const TIPO_LABEL: Record<TipoMapeoIngesta, string> = {
  linea: 'Líneas',
  sku: 'SKUs',
  causal: 'Causales',
};

export function MapeosSeccion({
  lineas,
  causales,
}: {
  lineas: OpcionLinea[];
  causales: OpcionCausal[];
}) {
  const mapeos = useMapeosIngesta();
  const crear = useCrearMapeoIngesta();
  const eliminar = useEliminarMapeoIngesta();
  const [tipo, setTipo] = useState<TipoMapeoIngesta>('linea');
  const [codigo, setCodigo] = useState('');
  const [destino, setDestino] = useState('');
  const [lineaSku, setLineaSku] = useState('');
  const [aBorrar, setABorrar] = useState<string | null>(null);
  const skus = useSkusByLinea(tipo === 'sku' && lineaSku ? lineaSku : null);

  const filas = (mapeos.data ?? []).filter((m) => m.tipo === tipo);
  const opciones: { id: string; etiqueta: string }[] =
    tipo === 'linea'
      ? lineas.map((l) => ({ id: l.id, etiqueta: `${l.nombre} (${l.codigo}) · ${l.planta}` }))
      : tipo === 'causal'
        ? causales
        : (skus.data ?? []).map((s) => ({
            id: s.id,
            etiqueta: `${s.codigo_cliente} · ${s.descripcion}`,
          }));

  function agregar() {
    const c = codigo.trim();
    if (!c || !destino) return;
    crear.mutate(
      { tipo, codigo_externo: c, destino_id: destino },
      {
        onSuccess: () => {
          toast.success('Mapeo agregado');
          setCodigo('');
          setDestino('');
        },
        onError: (e) => toast.error(mensajeError(e)),
      },
    );
  }

  return (
    <Seccion
      titulo="Mapeos"
      descripcion="Traducen los códigos que envía la máquina a las líneas, SKUs y causales de JIDOKAP. Un evento con un código sin mapeo se rechaza."
    >
      <SegmentedControl<TipoMapeoIngesta>
        value={tipo}
        onChange={(t) => {
          setTipo(t);
          setDestino('');
        }}
        options={(['linea', 'sku', 'causal'] as const).map((t) => ({
          value: t,
          label: TIPO_LABEL[t],
        }))}
      />
      {mapeos.isLoading ? (
        <p className="text-sm text-fg-tertiary">Cargando…</p>
      ) : filas.length === 0 ? (
        <p className="text-sm text-fg-tertiary">Sin mapeos de {TIPO_LABEL[tipo].toLowerCase()}.</p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Código de la máquina</TH>
              <TH>En JIDOKAP</TH>
              <TH />
            </TR>
          </THead>
          <TBody>
            {filas.map((m: MapeoIngesta) => (
              <TR key={m.id}>
                <TD className="font-mono text-[12px] text-fg">{m.codigo_externo}</TD>
                <TD className="text-fg-secondary">{m.destino_nombre || '—'}</TD>
                <TD align="right">
                  {aBorrar === m.id ? (
                    <span className="inline-flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="danger"
                        loading={eliminar.isPending}
                        onClick={() =>
                          eliminar.mutate(m.id, {
                            onSuccess: () => {
                              toast.success('Mapeo eliminado');
                              setABorrar(null);
                            },
                            onError: (e) => toast.error(mensajeError(e)),
                          })
                        }
                      >
                        Confirmar
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setABorrar(null)}>
                        Cancelar
                      </Button>
                    </span>
                  ) : (
                    <Button size="sm" variant="ghost" onClick={() => setABorrar(m.id)}>
                      Quitar
                    </Button>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}
      <div className="flex flex-wrap items-end gap-2 border-t border-border pt-3">
        <label className="flex flex-col gap-1 text-[11px] text-fg-tertiary">
          Código de la máquina
          <Input
            value={codigo}
            maxLength={64}
            onChange={(e) => setCodigo(e.target.value)}
            className="h-9 w-44 font-mono"
          />
        </label>
        {tipo === 'sku' ? (
          <label className="flex flex-col gap-1 text-[11px] text-fg-tertiary">
            Línea del SKU
            <Select
              value={lineaSku}
              onChange={(e) => {
                setLineaSku(e.target.value);
                setDestino('');
              }}
              className="h-9 w-48"
            >
              <option value="">— Elija —</option>
              {lineas.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.nombre}
                </option>
              ))}
            </Select>
          </label>
        ) : null}
        <label className="flex min-w-[14rem] flex-1 flex-col gap-1 text-[11px] text-fg-tertiary">
          {tipo === 'linea' ? 'Línea' : tipo === 'sku' ? 'SKU' : 'Causal específica'}
          <Select value={destino} onChange={(e) => setDestino(e.target.value)} className="h-9">
            <option value="">— Elija —</option>
            {opciones.map((o) => (
              <option key={o.id} value={o.id}>
                {o.etiqueta}
              </option>
            ))}
          </Select>
        </label>
        <Button
          size="sm"
          className="h-9"
          onClick={agregar}
          loading={crear.isPending}
          disabled={!codigo.trim() || !destino}
        >
          Agregar
        </Button>
      </div>
    </Seccion>
  );
}

// ─── Parámetros ─────────────────────────────────────────────────────────────

export function ParametrosSeccion({
  config,
  causales,
}: {
  config: IngestaConfig;
  causales: OpcionCausal[];
}) {
  const guardar = useGuardarIngestaConfig();
  const [f, setF] = useState(() => aForm(config));
  useEffect(() => setF(aForm(config)), [config]);
  const [error, setError] = useState<string | null>(null);

  function enviar() {
    setError(null);
    const latido = Number(f.ventana_latido_seg);
    const gracia = Number(f.ventana_gracia_min);
    const reloj = Number(f.tolerancia_reloj_seg);
    if (!Number.isInteger(latido) || latido < 10 || latido > 3600)
      return setError('La ventana de latido va de 10 a 3600 segundos.');
    if (!Number.isInteger(gracia) || gracia < 1 || gracia > 1440)
      return setError('La ventana de gracia va de 1 a 1440 minutos.');
    if (!Number.isInteger(reloj) || reloj < 0 || reloj > 3600)
      return setError('La tolerancia de reloj va de 0 a 3600 segundos.');
    guardar.mutate(
      {
        causal_pendiente_id: f.causal_pendiente_id || null,
        ventana_latido_seg: latido,
        ventana_gracia_min: gracia,
        tolerancia_reloj_seg: reloj,
      },
      {
        onSuccess: () => toast.success('Configuración guardada'),
        onError: (e) => setError(mensajeError(e)),
      },
    );
  }

  return (
    <Seccion
      titulo="Parámetros"
      descripcion="Causal con la que nacen los paros que la máquina no sabe clasificar, y las ventanas que deciden la señal y hasta cuándo se aceptan datos atrasados."
      accion={
        <Button size="sm" onClick={enviar} loading={guardar.isPending}>
          Guardar
        </Button>
      }
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5 sm:col-span-2">
          <Label htmlFor="cfg-causal">Causal pendiente</Label>
          <Select
            id="cfg-causal"
            value={f.causal_pendiente_id}
            onChange={(e) => setF({ ...f, causal_pendiente_id: e.target.value })}
          >
            <option value="">— Sin configurar —</option>
            {causales.map((c) => (
              <option key={c.id} value={c.id}>
                {c.etiqueta}
              </option>
            ))}
          </Select>
        </div>
        <CampoNumero
          id="cfg-latido"
          label="Ventana de latido (s)"
          ayuda="Sin latido en este tiempo, la línea pasa a «sin señal»."
          value={f.ventana_latido_seg}
          onChange={(v) => setF({ ...f, ventana_latido_seg: v })}
        />
        <CampoNumero
          id="cfg-gracia"
          label="Ventana de gracia (min)"
          ayuda="Datos atrasados dentro de esta ventana se re-consolidan."
          value={f.ventana_gracia_min}
          onChange={(v) => setF({ ...f, ventana_gracia_min: v })}
        />
        <CampoNumero
          id="cfg-reloj"
          label="Tolerancia de reloj (s)"
          ayuda="Diferencia admitida entre el reloj de la máquina y el del servidor."
          value={f.tolerancia_reloj_seg}
          onChange={(v) => setF({ ...f, tolerancia_reloj_seg: v })}
        />
      </div>
      {error ? (
        <p role="alert" className="text-xs text-crit">
          {error}
        </p>
      ) : null}
    </Seccion>
  );
}

function aForm(c: IngestaConfig) {
  return {
    causal_pendiente_id: c.causal_pendiente_id ?? '',
    ventana_latido_seg: String(c.ventana_latido_seg),
    ventana_gracia_min: String(c.ventana_gracia_min),
    tolerancia_reloj_seg: String(c.tolerancia_reloj_seg),
  };
}

function CampoNumero({
  id,
  label,
  ayuda,
  value,
  onChange,
}: {
  id: string;
  label: string;
  ayuda: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type="number"
        inputMode="numeric"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="tabular-nums"
      />
      <span className="text-[11px] text-fg-tertiary">{ayuda}</span>
    </div>
  );
}

// ─── Líneas: fuente de captura y estándar de cambio ─────────────────────────

type LineaVigente = AdminIngestaResumen['lineas'][number];
type SkuVigente = AdminIngestaResumen['skus'][number];

export function LineasSeccion({ lineas }: { lineas: OpcionLinea[] }) {
  const resumen = useAdminIngestaResumen();
  const vigentes = new Map((resumen.data?.lineas ?? []).map((l) => [l.id, l]));
  return (
    <Seccion
      titulo="Líneas"
      descripcion="Fuente de captura de cada línea y minutos estándar de un cambio de referencia. Para pasar a híbrida o máquina, los SKUs de la línea no pueden usar golpes por minuto y deben tener personas estándar por turno."
    >
      {lineas.length === 0 ? (
        <p className="text-sm text-fg-tertiary">Sin líneas activas.</p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Línea</TH>
              <TH>Fuente de captura</TH>
              <TH>Cambio estándar (min)</TH>
              <TH />
            </TR>
          </THead>
          <TBody>
            {lineas.map((l) => (
              <FilaLinea key={l.id} linea={l} vigente={vigentes.get(l.id)} />
            ))}
          </TBody>
        </Table>
      )}
    </Seccion>
  );
}

function FilaLinea({ linea, vigente }: { linea: OpcionLinea; vigente?: LineaVigente }) {
  const actualizar = useActualizarFuenteCapturaLinea();
  const [fuente, setFuente] = useState<FuenteCapturaLinea | ''>('');
  const [minutos, setMinutos] = useState('');
  const [guardado, setGuardado] = useState<LineaFuenteCapturaResultado | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fuenteVigente = guardado?.fuente_captura ?? vigente?.fuente_captura;
  const minutosVigentes = guardado
    ? guardado.minutos_cambio_referencia_estandar
    : vigente?.minutos_cambio_referencia_estandar;
  // El endpoint exige fuente_captura: sin elegirla se reenvía la vigente.
  const fuenteEfectiva = fuente || (fuenteVigente as FuenteCapturaLinea | undefined);

  function enviar() {
    setError(null);
    if (!fuenteEfectiva) return setError('Elija la fuente de captura.');
    const m = minutos.trim();
    const n = Number(m);
    if (m && (!Number.isInteger(n) || n < 0 || n > 1440))
      return setError('Minutos de 0 a 1440 (entero).');
    actualizar.mutate(
      {
        lineaId: linea.id,
        body: {
          fuente_captura: fuenteEfectiva,
          ...(m ? { minutos_cambio_referencia_estandar: n } : {}),
        },
      },
      {
        onSuccess: (r) => {
          setGuardado(r);
          setFuente('');
          setMinutos('');
          toast.success(`Línea ${linea.nombre} actualizada`);
        },
        onError: (e) => setError(mensajeError(e)),
      },
    );
  }

  return (
    <>
      <TR>
        <TD>
          <p className="text-sm text-fg">{linea.nombre}</p>
          <p className="text-[11px] text-fg-tertiary">
            {linea.codigo} · {linea.planta}
          </p>
        </TD>
        <TD>
          <div className="flex items-center gap-2">
            <Select
              aria-label={`Fuente de captura de ${linea.nombre}`}
              value={fuente}
              onChange={(e) => setFuente(e.target.value as FuenteCapturaLinea | '')}
              className="h-9 w-40"
            >
              <option value="">— Sin cambios —</option>
              {(['manual', 'hibrida', 'maquina'] as const).map((f) => (
                <option key={f} value={f}>
                  {ETIQUETA_FUENTE[f]}
                </option>
              ))}
            </Select>
            {fuenteVigente ? (
              <Badge tone="info">
                {ETIQUETA_FUENTE[fuenteVigente as FuenteCapturaLinea] ?? fuenteVigente}
              </Badge>
            ) : null}
          </div>
        </TD>
        <TD>
          <Input
            aria-label={`Minutos estándar de cambio de ${linea.nombre}`}
            type="number"
            inputMode="numeric"
            placeholder={minutosVigentes != null ? String(minutosVigentes) : 'Sin cambios'}
            value={minutos}
            onChange={(e) => setMinutos(e.target.value)}
            className="h-9 w-28 tabular-nums"
          />
        </TD>
        <TD align="right">
          <Button
            size="sm"
            variant="secondary"
            onClick={enviar}
            loading={actualizar.isPending}
            disabled={!fuente && !minutos.trim()}
          >
            Guardar
          </Button>
        </TD>
      </TR>
      {error ? (
        <TR>
          <TD colSpan={4} className="pt-0">
            <p role="alert" className="rounded bg-crit-soft px-3 py-2 text-xs text-crit">
              {error}
            </p>
          </TD>
        </TR>
      ) : null}
    </>
  );
}

// ─── Peso nominal por SKU ───────────────────────────────────────────────────

export function PesoSkuSeccion({ lineas }: { lineas: OpcionLinea[] }) {
  const [lineaId, setLineaId] = useState('');
  const skus = useSkusByLinea(lineaId || null);
  const resumen = useAdminIngestaResumen();
  const vigentes = new Map((resumen.data?.skus ?? []).map((s) => [s.id, s]));
  const ordenados = useMemo(
    () => [...(skus.data ?? [])].sort((a, b) => a.codigo_cliente.localeCompare(b.codigo_cliente)),
    [skus.data],
  );
  return (
    <Seccion
      titulo="Peso por SKU"
      descripcion="Peso nominal y tolerancia por unidad para medir el sobrellenado; el costo de referencia por kg permite valorar el producto regalado. Todos son opcionales."
    >
      <label className="flex max-w-xs flex-col gap-1 text-[11px] text-fg-tertiary">
        Línea
        <Select value={lineaId} onChange={(e) => setLineaId(e.target.value)} className="h-9">
          <option value="">— Elija una línea para ver sus SKUs —</option>
          {lineas.map((l) => (
            <option key={l.id} value={l.id}>
              {l.nombre} · {l.planta}
            </option>
          ))}
        </Select>
      </label>
      {!lineaId ? null : skus.isLoading ? (
        <p className="text-sm text-fg-tertiary">Cargando SKUs…</p>
      ) : ordenados.length === 0 ? (
        <p className="text-sm text-fg-tertiary">La línea no tiene SKUs con capacidad.</p>
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>SKU</TH>
              <TH>Peso nominal (g)</TH>
              <TH>Tolerancia (g)</TH>
              <TH>Costo de referencia ($/kg)</TH>
              <TH />
            </TR>
          </THead>
          <TBody>
            {ordenados.map((s) => (
              <FilaPesoSku
                key={s.id}
                skuId={s.id}
                codigo={s.codigo_cliente}
                descripcion={s.descripcion}
                vigente={vigentes.get(s.id)}
              />
            ))}
          </TBody>
        </Table>
      )}
    </Seccion>
  );
}

function FilaPesoSku({
  skuId,
  codigo,
  descripcion,
  vigente,
}: {
  skuId: string;
  codigo: string;
  descripcion: string;
  vigente?: SkuVigente;
}) {
  const actualizar = useActualizarPesoSku();
  const [f, setF] = useState({ peso: '', tolerancia: '', costo: '' });
  const [guardado, setGuardado] = useState<SkuPesoResultado | null>(null);
  const [error, setError] = useState<string | null>(null);

  function enviar() {
    setError(null);
    const body: SkuPesoUpdate = {};
    const leer = (v: string): number | null | undefined => {
      const t = v.trim();
      if (!t) return undefined;
      const n = Number(t.replace(',', '.'));
      return Number.isFinite(n) ? n : null;
    };
    const peso = leer(f.peso);
    const tol = leer(f.tolerancia);
    const costo = leer(f.costo);
    if (peso === null || tol === null || costo === null) return setError('Use solo números.');
    if (peso !== undefined) {
      if (peso <= 0) return setError('El peso nominal debe ser mayor que cero.');
      body.peso_nominal_g = peso;
    }
    if (tol !== undefined) {
      if (tol < 0) return setError('La tolerancia no puede ser negativa.');
      body.tolerancia_g = tol;
    }
    if (costo !== undefined) {
      if (costo < 0) return setError('El costo no puede ser negativo.');
      body.costo_referencia_kg = costo;
    }
    if (Object.keys(body).length === 0) return setError('Escriba al menos un valor.');
    actualizar.mutate(
      { skuId, body },
      {
        onSuccess: (r) => {
          setGuardado(r);
          setF({ peso: '', tolerancia: '', costo: '' });
          toast.success(`SKU ${codigo} actualizado`);
        },
        onError: (e) => setError(mensajeError(e)),
      },
    );
  }

  const ph = (v: number | null | undefined) =>
    v === null || v === undefined ? 'Sin cambios' : String(v);

  return (
    <>
      <TR>
        <TD>
          <p className="font-mono text-[12px] text-fg">{codigo}</p>
          <p className="text-[11px] text-fg-tertiary">{descripcion}</p>
        </TD>
        <TD>
          <Input
            aria-label={`Peso nominal de ${codigo}`}
            inputMode="decimal"
            placeholder={ph((guardado ?? vigente)?.peso_nominal_g)}
            value={f.peso}
            onChange={(e) => setF({ ...f, peso: e.target.value })}
            className="h-9 w-28 tabular-nums"
          />
        </TD>
        <TD>
          <Input
            aria-label={`Tolerancia de ${codigo}`}
            inputMode="decimal"
            placeholder={ph((guardado ?? vigente)?.tolerancia_g)}
            value={f.tolerancia}
            onChange={(e) => setF({ ...f, tolerancia: e.target.value })}
            className="h-9 w-24 tabular-nums"
          />
        </TD>
        <TD>
          <Input
            aria-label={`Costo de referencia de ${codigo}`}
            inputMode="decimal"
            placeholder={ph((guardado ?? vigente)?.costo_referencia_kg)}
            value={f.costo}
            onChange={(e) => setF({ ...f, costo: e.target.value })}
            className="h-9 w-32 tabular-nums"
          />
        </TD>
        <TD align="right">
          <Button size="sm" variant="secondary" onClick={enviar} loading={actualizar.isPending}>
            Guardar
          </Button>
        </TD>
      </TR>
      {error ? (
        <TR>
          <TD colSpan={5} className="pt-0">
            <p role="alert" className="text-xs text-crit">
              {error}
            </p>
          </TD>
        </TR>
      ) : null}
    </>
  );
}
